/**
 * Parent-side helper for WellSync pharmacy.js iframe (see https://github.com/wellsync/pharmacy.js public/client.js).
 * Loads @iframe-resizer/parent, mounts the CareConnect UI in an iframe, and signs SIGN_REQUEST via WebCrypto.
 */

function loadScriptAsync(src) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

export default class PharmacyOrderClient {
  constructor(elementSelector, session, options) {
    this._session = session;
    this._options = options || {};
    this._privateKey = null;
    this._iframe = null;
    this._boundMessageHandler = this.handleMessage.bind(this);
    window.addEventListener("message", this._boundMessageHandler, false);

    const widgetBase = new URL(
      this._options.widgetIframeOrigin || "https://pharmacy.js.wellsync.io",
    );
    widgetBase.searchParams.append("session", session);
    widgetBase.searchParams.append("case", this._options.case);
    widgetBase.searchParams.append("clinic", this._options.clinic);
    widgetBase.searchParams.append("clinician", this._options.clinician);
    widgetBase.searchParams.append("patient", this._options.patient);

    if (this._options.environment) {
      widgetBase.searchParams.append("environment", this._options.environment);
    }

    const drugs = this._options.drugs;
    if (drugs && typeof drugs === "object" && !Array.isArray(drugs)) {
      if (drugs.message) {
        widgetBase.searchParams.append("drugs.message", drugs.message);
      }
      if (Array.isArray(drugs.selected)) {
        if (drugs.selected.length > 1) {
          throw new Error(
            "WellSync pharmacy.js only supports one drug at a time",
          );
        }
        widgetBase.searchParams.append(
          "drugs.selected",
          drugs.selected.join(","),
        );
      }
    }

    this._iframe = document.createElement("iframe");
    this._iframe.style.border = "none";
    this._iframe.width = "100%";
    this._iframe.src = widgetBase.href;

    this._init(elementSelector, this._options);
  }

  async _init(elementSelector, options) {
    await loadScriptAsync(
      "https://cdn.jsdelivr.net/npm/@iframe-resizer/parent@5.3.2",
    );

    this._privateKey = await this._importPrivateKey(options.privateKey);

    const target = document.querySelector(elementSelector);
    if (!target) {
      throw new Error("Element not found to mount the pharmacy order widget.");
    }
    target.appendChild(this._iframe);

    const resize =
      typeof window.iFrameResize === "function"
        ? window.iFrameResize
        : typeof window.iframeResize === "function"
        ? window.iframeResize
        : null;
    if (resize) {
      resize({ license: "GPLv3", waitForLoad: true }, this._iframe);
    }
  }

  async _importPrivateKey(pemKey) {
    const pemContents = pemKey
      .replace(/-----BEGIN PRIVATE KEY-----/, "")
      .replace(/-----END PRIVATE KEY-----/, "")
      .replace(/\n/g, "")
      .replace(/\r/g, "");

    const binaryKey = atob(pemContents);
    const binaryKeyBuffer = new Uint8Array(binaryKey.length);
    for (let i = 0; i < binaryKey.length; i++) {
      binaryKeyBuffer[i] = binaryKey.charCodeAt(i);
    }

    return crypto.subtle.importKey(
      "pkcs8",
      binaryKeyBuffer.buffer,
      {
        name: "RSASSA-PKCS1-v1_5",
        hash: { name: "SHA-256" },
      },
      true,
      ["sign"],
    );
  }

  async handleMessage(event) {
    if (!this._privateKey) {
      return;
    }

    const { type, data, requestId } = event.data || {};

    if (type === "SIGN_REQUEST" && data) {
      try {
        const signedResponse = await this.sign(data);
        event.source.postMessage(
          {
            type: "SIGN_RESPONSE",
            data: signedResponse,
            requestId,
          },
          event.origin,
        );
      } catch (error) {
        event.source.postMessage(
          {
            type: "SIGN_ERROR",
            data: error.message,
            requestId,
          },
          event.origin,
        );
      }
    }
  }

  async sign(data) {
    if (!this._privateKey) {
      throw new Error("Private key not initialized.");
    }

    const encoder = new TextEncoder();
    const signature = await crypto.subtle.sign(
      {
        name: "RSASSA-PKCS1-v1_5",
      },
      this._privateKey,
      encoder.encode(data),
    );

    const sigBytes = new Uint8Array(signature);
    let binary = "";
    for (let i = 0; i < sigBytes.byteLength; i++) {
      binary += String.fromCharCode(sigBytes[i]);
    }

    return {
      signature: btoa(binary),
      fingerprint: await this.hash(data),
    };
  }

  async hash(string) {
    const utf8 = new TextEncoder().encode(string);
    const hashBuffer = await crypto.subtle.digest("SHA-256", utf8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }

  destroy() {
    window.removeEventListener("message", this._boundMessageHandler, false);
    if (this._iframe && this._iframe.parentNode) {
      this._iframe.parentNode.removeChild(this._iframe);
    }
    this._iframe = null;
    this._privateKey = null;
  }
}
